import React, { useContext, useState } from 'react';
import { EDAutoCompleteEdit } from '../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import EDDialog from '../../../../../../BaseComponents/Dialog/EDDialog';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import {
  ActiveRoutineContext,
  defaultRoutineLayoutId,
  defaultRoutineLayoutName,
  getActiveRoutineQueryVariables,
} from '../../../../../../Features/Routing/ActiveRoutine/ActiveRoutineContext';
import { UserRoutineLayoutsQuery_me_dbsettings_sections } from './Queries/_QueryTypes/UserRoutineLayoutsQuery';
import removeUserRoutineLayoutMutation from './Mutations/RemoveUserRoutineLayoutMutation.gql';
import {
  RemoveUserRoutineLayoutMutation,
  RemoveUserRoutineLayoutMutationVariables,
} from './Mutations/_QueryTypes/RemoveUserRoutineLayoutMutation';
import { useMutation } from '@apollo/client';
import { ActiveDatabaseContext } from '../../../../../ActiveDatabaseContext/ActiveDatabaseContext';
import { padRoutineId } from '../../../../../../Utils/dbSettingsUtils';
import { RutinSettingsContext } from '../../../../../../Features/Delsystem/_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import { ErrorDialogContext } from '../../../../../../BaseComponents/ErrorDialogManager/ErrorDialogManager';
import {
  ActiveRoutineQuery,
  ActiveRoutineQueryVariables,
} from '../../../../../../Features/Routing/ActiveRoutine/_QueryTypes/ActiveRoutineQuery';
import activeRoutineQuery from '../../../../../../Features/Routing/ActiveRoutine/ActiveRoutineQuery.gql';
import { clone } from 'ramda';
import { KodplanContext } from '../../../../../KodplanManager/Context/KodplanContext';

export interface IKolumnSetting {
  id: string;
  name: string;
}

interface IProps {
  selectedKolumnSetting: string | null;
  setSelectedKolumnSetting: (id: string | null) => void;
  favouriteKolumnSetting: string | null | undefined;
  setFavouriteKolumnSetting: (id: string | null) => void;
  collectionName: string;
}

export const userRoutineLayoutsSettingSectionName = 'UserRoutineLayouts';
export const favouriteRoutineLayoutSettingValueName =
  'NA_FavouriteRoutineLayout';

enum KolumnSettingCategory {
  egen = 'Sparade inställningar',
  standard = 'Standardinställningar',
}
interface IKolumnSettingOption
  extends Omit<UserRoutineLayoutsQuery_me_dbsettings_sections, '__typename'> {
  category: KolumnSettingCategory;
}

const KolumnSettingSelect: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const activeRoutineContext = useContext(ActiveRoutineContext);
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const { activeRoutine } = useContext(RutinSettingsContext);
  const errorDialogContext = useContext(ErrorDialogContext);
  const kodplanContext = useContext(KodplanContext);
  const activeRoutineId = activeRoutine?.routine?.rutinID;
  const [optionToDelete, setOptionToDelete] =
    useState<IKolumnSettingOption | null>(null);

  const [removeUserRoutineLayout, { loading: removeLayoutLoading }] =
    useMutation<
      RemoveUserRoutineLayoutMutation,
      RemoveUserRoutineLayoutMutationVariables
    >(removeUserRoutineLayoutMutation, {
      onError: error => {
        errorDialogContext.error('Kunde inte ta bort inställning', error);
      },
      onCompleted: () => {
        setOptionToDelete(null);
        if (optionToDelete?.id === props.selectedKolumnSetting) {
          props.setSelectedKolumnSetting(defaultRoutineLayoutId);
        }
      },
      update: (cache, result) => {
        if (result.errors) {
          return;
        }
        const activeRoutineQueryVariables = getActiveRoutineQueryVariables(
          activeDatabaseContext.activeDatabase,
          activeRoutineId,
          props.collectionName,
          kodplanContext.activeKodplan?.id
        );
        const oldData = cache.readQuery<
          ActiveRoutineQuery,
          ActiveRoutineQueryVariables
        >({
          query: activeRoutineQuery,
          variables: activeRoutineQueryVariables,
        });

        if (oldData) {
          const newData = clone(oldData);
          const routineLayoutSection = newData?.me?.[0]?.routineLayouts?.find(
            routineLayout =>
              routineLayout.name === userRoutineLayoutsSettingSectionName
          );

          if (routineLayoutSection) {
            routineLayoutSection.sections =
              routineLayoutSection.sections?.filter(
                section => section.id !== optionToDelete?.id
              ) ?? [];

            cache.writeQuery<ActiveRoutineQuery, ActiveRoutineQueryVariables>({
              query: activeRoutineQuery,
              data: newData,
              variables: activeRoutineQueryVariables,
            });
          }
        }
      },
    });

  const options: IKolumnSettingOption[] | undefined =
    activeRoutineContext?.sparadeLayouter.map(savedUserRoutineLayout => ({
      id: savedUserRoutineLayout.id,
      name: savedUserRoutineLayout.name,
      category: KolumnSettingCategory.egen,
    }));
  options?.push({
    category: KolumnSettingCategory.standard,
    id: defaultRoutineLayoutId,
    name: defaultRoutineLayoutName,
  });

  const value =
    options?.find(option => option.id === props.selectedKolumnSetting) ?? null;

  return (
    <>
      <EDAutoCompleteEdit<IKolumnSettingOption>
        label={'Vald inställning'}
        options={options ?? []}
        getOptionLabel={option => {
          return option.name ?? '';
        }}
        onChange={option => {
          let newSelectedKolumnSetting: string | null = null;
          if (option && !Array.isArray(option)) {
            newSelectedKolumnSetting = option.id;
          }
          props.setSelectedKolumnSetting(newSelectedKolumnSetting);
        }}
        hideClear
        width={280}
        isHeadlineSelection
        inputDarkTheme
        getOptionSelected={(option, value) => {
          return option.id === value.id;
        }}
        value={value}
        groupBy={option => option.category}
        actions={[
          {
            id: 'Favourite',
            tooltip: option =>
              props.favouriteKolumnSetting === option.id
                ? 'Välj bort favorit'
                : 'Välj som favorit',
            icon: option =>
              props.favouriteKolumnSetting === option.id
                ? 'star'
                : 'star_border',
            onClick: option => {
              props.setFavouriteKolumnSetting(
                props.favouriteKolumnSetting === option.id ? null : option.id
              );
            },
            hide: option => option.category === KolumnSettingCategory.standard,
          },
          {
            id: 'Delete',
            tooltip: 'Ta bort',
            icon: 'delete',
            onClick: option => {
              setOptionToDelete(option);
            },
            hide: option => option.category === KolumnSettingCategory.standard,
          },
        ]}
      />
      <EDDialog
        title="Ta bort sparad inställning"
        open={Boolean(optionToDelete)}
        onClose={() => {
          setOptionToDelete(null);
        }}
        footerActions={[
          {
            label: 'Avbryt',
            onClick: () => {
              setOptionToDelete(null);
            },
          },
          {
            label: removeLayoutLoading ? 'Tar bort' : 'Ta bort',
            onClick: () => {
              if (activeDatabaseContext.activeDatabase && activeRoutineId) {
                if (optionToDelete && optionToDelete.name) {
                  removeUserRoutineLayout({
                    variables: {
                      dbName: activeDatabaseContext.activeDatabase,
                      sectionName: `Forms/${padRoutineId(
                        activeRoutineId
                      )}/${userRoutineLayoutsSettingSectionName}/${
                        optionToDelete.name
                      }`,
                    },
                  });
                } else {
                  throw new Error(
                    `Could not delete UserRoutineLayout ${optionToDelete}`
                  );
                }
              } else {
                throw new Error('No active database or active routine');
              }
            },
            buttonProps: {
              disabled: removeLayoutLoading,
            },
          },
        ]}
      >
        <EDTypography>
          Du har markerat att du vill ta bort din sparade inställning{' '}
          <EDTypography component="span" bold>
            {optionToDelete?.name}
          </EDTypography>
          <br />
          Vill du verkligen ta bort den?
        </EDTypography>
      </EDDialog>
    </>
  );
};

export default KolumnSettingSelect;
